import React, { useEffect, useState } from 'react';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  IconButton,
  Typography,
} from '@mui/material';
import { Add, Remove } from '@mui/icons-material';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { LoadingService } from 'react-ui-components/components/LoadingService';
import { PopupService } from 'react-ui-components/components/PopupService';
import { getData } from '../../components/core/CreateEditOptions/getData';
import { LanguageConfig } from 'react-ui-components/components/Content/ContentTypes';

interface KeyValuePair {
  key: string;
  value: string;
}

interface AddTranslationPopupProps {
  close: () => void;
  onSuccess?: () => void;
}

const AddTranslationPopup: React.FC<AddTranslationPopupProps> = ({ close, onSuccess }) => {
  const [languages, setLanguages] = useState<LanguageConfig[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [pairs, setPairs] = useState<KeyValuePair[]>([{ key: '', value: '' }]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLanguages();
  }, []);

  const fetchLanguages = async () => {
    getData({
      setData: (data: any) => {
        if (data) {
          const languages = Object.values(data).map((item: any) => ({
            code: item.code,
            name: item.value,
            nativeName: item.value,
            flag: '',
          }));
          setLanguages(languages);
          if (languages.length > 0) {
            setSelectedLanguage(languages[0].code);
          }
        }
      },
      path: "app-configurations/languages",
      getMap: (item: any) => item,
      setLoading,
    });
  };

  const handleAddPair = () => {
    setPairs([...pairs, { key: '', value: '' }]);
  };

  const handleRemovePair = (index: number) => {
    setPairs(pairs.filter((_, i) => i !== index));
  };

  const handlePairChange = (index: number, field: 'key' | 'value', value: string) => {
    const newPairs = [...pairs];
    newPairs[index][field] = value;
    setPairs(newPairs);
  };

  const handleSubmit = async () => {
    if (!selectedLanguage || pairs.some(p => !p.key.trim() || !p.value.trim())) {
      PopupService.pushPopup('Please fill in all fields.');
      return;
    }

    try {
      LoadingService.setLoading(true, 'Adding translations...');
      const docRef = doc(db, 'app-configurations', 'translations');
      const updates: { [key: string]: string } = {};
      pairs.forEach(pair => {
        updates[`${selectedLanguage}.${pair.key}`] = pair.value;
      });
      await updateDoc(docRef, updates);
      PopupService.pushPopup('Translations added successfully!');
      onSuccess?.();
      close();
    } catch (error: any) {
      PopupService.pushPopup(error.message);
    } finally {
      LoadingService.setLoading(false);
    }
  };

  return (
    <Box sx={{ minWidth: 400, p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Add Translations
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel>Select Language</InputLabel>
        <Select
          value={selectedLanguage}
          onChange={(e) => setSelectedLanguage(e.target.value)}
          disabled={loading}
        >
          {languages.map(lang => (
            <MenuItem key={lang.code} value={lang.code}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {lang.flag && <span>{lang.flag}</span>}
                {lang.nativeName || lang.name}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {pairs.map((pair, index) => (
        <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TextField
            label="Key"
            value={pair.key}
            onChange={(e) => handlePairChange(index, 'key', e.target.value)}
            sx={{ mr: 1, flex: 1 }}
          />
          <TextField
            label="Value"
            value={pair.value}
            onChange={(e) => handlePairChange(index, 'value', e.target.value)}
            sx={{ mr: 1, flex: 1 }}
          />
          <IconButton onClick={() => handleRemovePair(index)} disabled={pairs.length === 1}>
            <Remove />
          </IconButton>
        </Box>
      ))}
      <Button onClick={handleAddPair} startIcon={<Add />} sx={{ mb: 2 }}>
        Add Another Pair
      </Button>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button onClick={close} color="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Add Translations
        </Button>
      </Box>
    </Box>
  );
};

export default AddTranslationPopup;